import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { axiosInstance } from '../../config';
import { Box, Container, Typography, TextField, Button, Card, CardContent, Switch, FormControlLabel, CircularProgress } from '@mui/material';
import TopBar from '../../components/topbar/Topbar';
import TopBarMobile from '../../components/topbar/TopbarMobile';
import './subscriptions.css';

export default function Subscriptions() {
  const { user: currentUser } = useContext(AuthContext);
  const [subscriptionEnabled, setSubscriptionEnabled] = useState(false);
  const [price, setPrice] = useState('');
  const [description, setDescription] = useState('');
  const [stripeConnected, setStripeConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  useEffect(() => {
    const updateIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);
    return () => window.removeEventListener('resize', updateIsMobile);
  }, []);

  useEffect(() => {
    const fetchCreatorSettings = async () => {
      try {
        const res = await axiosInstance.get(`/users/creator-settings/${currentUser._id}`);
        setSubscriptionEnabled(res.data.subscriptionEnabled || false);
        setPrice(res.data.subscriptionPrice || '');
        setDescription(res.data.subscriptionDescription || '');
        const hasStripeAccount = Boolean(res.data.stripeConnectAccountId);
        setStripeConnected(hasStripeAccount);
        if (hasStripeAccount && new URLSearchParams(window.location.search).get('setup') === 'complete') {
          setShowSuccessAlert(true);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching creator settings:', error);
        setLoading(false);
        setStripeConnected(false);
      }
    };

    if (currentUser?._id) {
      fetchCreatorSettings();
    }
  }, [currentUser?._id]);

  const handleSave = async () => {
    try {
      if (subscriptionEnabled && price) {
        const priceResponse = await axiosInstance.post('/v1/stripe/create-subscription-price', {
          price: parseFloat(price),
          userId: currentUser._id,
          applicationFeePercent: 10
        });
      }

      await axiosInstance.post(`/users/update-creator-settings/${currentUser._id}`, {
        subscriptionEnabled,
        subscriptionPrice: price,
        subscriptionDescription: description
      });

      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings. Please try again.');
    }
  };

  const connectStripe = async () => {
    try {
      setConnecting(true);
      const response = await axiosInstance.post('/v1/stripe/connect-account', {
        userId: currentUser._id,
        returnUrl: window.location.href
      });
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error connecting Stripe:', error);
      alert('Failed to connect Stripe. Please try again.');
      setConnecting(false);
    }
  };

  const manageStripeAccount = async () => {
    try {
      const response = await axiosInstance.post('/v1/stripe/manage-account', {
        userId: currentUser._id
      });
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error opening Stripe dashboard:', error);
      alert('Failed to open Stripe dashboard. Please try again.');
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const setup = params.get('setup');
    const accountId = params.get('account');

    if (setup === 'complete' && accountId) {
      axiosInstance.post('/v1/stripe/verify-account', {
        userId: currentUser._id,
        accountId: accountId
      })
      .then(() => {
        setStripeConnected(true);
        setShowSuccessAlert(true);
      })
      .catch(error => {
        console.error('Error verifying account:', error);
        alert('Failed to complete Stripe setup. Please try again.');
      });

      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [currentUser._id]);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      {isMobile ? <TopBarMobile /> : <TopBar />}
      <div className="subscriptions">
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'white' }}>
            Subscription Settings
          </Typography>

          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Stripe Connection Status
              </Typography>
              {stripeConnected ? (
                <Box sx={{ mb: 2 }}>
                  <Typography color="success.main" sx={{ mb: 2 }}>
                    ✓ Your Stripe account is connected and ready to receive payments
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button 
                      variant="contained"
                      onClick={manageStripeAccount}
                      color="primary"
                    >
                      Manage Stripe Account
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Typography sx={{ mb: 2 }}>
                    Connect your Stripe account to start receiving subscription payments from your subscribers. 
                    <strong style={{ color: '#ff4444' }}> IMPORTANT: You must complete the entire Stripe setup process. 
                    Do not close or leave the page until setup is fully complete, or you'll need to start over.</strong>
                    <br/><br/>
                    <span style={{ fontSize: '0.9em', color: '#666' }}>
                      Having issues? Contact support at xsubscribe.bio@gmail.com for assistance.
                    </span>
                  </Typography>
                  <Button 
                    variant="contained" 
                    onClick={connectStripe}
                    disabled={connecting}
                    color="primary"
                  >
                    {connecting ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CircularProgress size={24} sx={{ mr: 1 }} color="inherit" />
                        Connecting to Stripe...
                      </Box>
                    ) : (
                      'Connect Stripe Account'
                    )}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <FormControlLabel
                control={
                  <Switch
                    checked={subscriptionEnabled}
                    onChange={(e) => setSubscriptionEnabled(e.target.checked)}
                    disabled={!stripeConnected}
                  />
                }
                label="Enable Subscriptions"
                sx={{ mb: 3 }}
              />

              <Box sx={{ mb: 3 }}>
                <TextField
                  label="Monthly Subscription Price (USD)"
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  disabled={!subscriptionEnabled || !stripeConnected}
                  fullWidth
                  InputProps={{
                    startAdornment: '$',
                    inputProps: { min: 1, step: 0.01 }
                  }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <TextField
                  label="Subscription Description"
                  multiline
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={!subscriptionEnabled || !stripeConnected}
                  fullWidth
                  placeholder="Describe what subscribers will get access to..."
                />
              </Box>

              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!stripeConnected}
                fullWidth
              >
                Save Settings
              </Button>
            </CardContent>
          </Card>
        </Container>
      </div>
    </div>
  );
} 